import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Anchor, Dropdown } from 'react-bootstrap';
import { HISTORY_ZERO_STATE } from '../../constants/HistoryConstants';
import { deleteChatHistoryRecord, archiveChatHistoryRecord } from '../../services/history';
import { useHistoryFunctions } from '../../providers/historyProvider';
import MoreIcon from '../../assets/icon-google-more-horz.svg?react';
import ArchiveIcon from '../../assets/icon-archive.svg?react';
import RenameIcon from '../../assets/icon-edit.svg?react';
import DeleteIcon from '../../assets/icon-google-delete.svg?react';
import TickIcon from '../../assets/icon-google-tick.svg?react';
import { formatResponse, generateChatSessionId } from '../../utils/chat';
import { CustomDropdownItem } from '../common/UI/CustomDropdownItem';
import useElysiaStore from '@/app/elysia-store';

export const ChatHistoryContextMenu = (props) => {
  const { chatConversationMetadata, setTitle, setShowInputForRename, pathname } = props;
  const setIsChatActive = useElysiaStore.getState().setIsChatActive;
  const setPromptInput = useElysiaStore.getState().setPromptInput;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setChatSessionId = useElysiaStore.getState().setChatSessionId;
  const setClearState = useElysiaStore.getState().setClearState;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setShowScrollIcon = useElysiaStore.getState().setShowScrollIcon;
  const setUploadedFileTitle = useElysiaStore.getState().setUploadedFileTitle;
  const setUploadedFile = useElysiaStore.getState().setUploadedFile;
  const setSources = useElysiaStore.getState().setSources;
  const setSelectedSources = useElysiaStore.getState().setSelectedSources;
  const setShowFileToasts = useElysiaStore.getState().setShowFileToasts;
  const resetAIApp = useElysiaStore((state) => state.resetAIApp);

  const navigate = useNavigate();
  const [showBadge, setShowBadge] = useState(false);
  const [badgeText, setBadgeText] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState(false);

  const historyFunctions = useHistoryFunctions();
  if (!historyFunctions) {
    throw new Error('History functions are not available.');
  }
  const { updateChatHistoryOnDelete, updateChatHistoryOnArchive } = historyFunctions;

  const startNewTopicChat = () => {
    setShowScrollIcon?.(false);
    setClearState?.(true);
    setNewChat?.(true);
    setChatSessionId?.(generateChatSessionId());
    setChatAgent?.('Generic');
    setActiveChatHistoryRecord(HISTORY_ZERO_STATE);
    navigate('/');
    setPromptInput('');
    setIsChatActive?.(false);
    setSessionPath?.('');
    setUploadedFileTitle?.('');
    setUploadedFile?.(null);
    setSources?.([]);
    setSelectedSources?.([]);
    setShowFileToasts?.(false);
    resetAIApp();
  };

  const renameTitle = () => {
    setTitle(formatResponse(chatConversationMetadata?.chat_history_title, true));
    setShowInputForRename(true);
  };

  const handleBadge = (badgeText: string) => {
    setBadgeText(badgeText);
    setShowBadge(true);
    const timeoutRef = setTimeout(() => {
      setShowBadge(false);
      clearTimeout(timeoutRef);
      if (badgeText === 'Deleted history') {
        updateChatHistoryOnDelete(chatConversationMetadata.session_id);
      } else {
        updateChatHistoryOnArchive(chatConversationMetadata.session_id);
      }
    }, 2000);
  };

  const handleDeleteHistoryClick = () => {
    let response;

    (async () => {
      try {
        response = await deleteChatHistoryRecord(chatConversationMetadata.session_id);
        if (pathname === chatConversationMetadata.session_id) {
          startNewTopicChat();
        }
        if (response) {
          handleBadge('Deleted history');
          setShowDropdown(false);
        }
      } catch (err) {
        console.error('error deleting chat record', err);
      }
    })();
  };

  const handleArchiveHistoryClick = () => {
    let response;
    (async () => {
      try {
        response = await archiveChatHistoryRecord(chatConversationMetadata.session_id);
        if (response) {
          handleBadge('Archived history');
          setShowDropdown(false);
        }
      } catch (err) {
        console.error('error archiving chat record', err);
      }
    })();
  };

  return (
    <>
      <Dropdown
        align="end"
        className="d-inline mx-2 more-options"
        show={showDropdown}
        onToggle={(isOpen) => setShowDropdown(isOpen)}
      >
        <Dropdown.Toggle
          as={Anchor}
          aria-label="more options"
          className="more-icon-dropdown"
          aria-haspopup="true"
        >
          <MoreIcon />
        </Dropdown.Toggle>

        <Dropdown.Menu className="dropdown-context-menu" role="menu">
          {!chatConversationMetadata.is_archived && (
            <CustomDropdownItem
              className="archive-dropdown-item"
              component="button"
              onClick={handleArchiveHistoryClick}
            >
              <div className="d-flex align-items-center">
                <ArchiveIcon />
                <span>Archive</span>
              </div>
            </CustomDropdownItem>
          )}
          <CustomDropdownItem
            className="rename-dropdown-item"
            component="button"
            onClick={renameTitle}
          >
            <div className="d-flex align-items-center">
              <RenameIcon />
              <span>Rename</span>
            </div>
          </CustomDropdownItem>
          <CustomDropdownItem
            className="delete-dropdown-item"
            component="button"
            onClick={handleDeleteHistoryClick}
          >
            <div className="d-flex align-items-center">
              <DeleteIcon />
              <span>Delete</span>
            </div>
          </CustomDropdownItem>
        </Dropdown.Menu>
      </Dropdown>
      <div aria-label="assertive">
        {showBadge && (
          <span className="history-badge position-absolute">
            <TickIcon />
            <span>{badgeText}</span>
          </span>
        )}
      </div>
    </>
  );
};
